import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { ListPreview } from "../schema/listPreview";

interface PreviewRunningProps {
	preview: ListPreview;
	onStop: () => void;
	isStopLoading: boolean;
}

export const PreviewRunning: FunctionComponent<PreviewRunningProps> = ({
	preview,
	onStop,
	isStopLoading,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`list.preview.${key}`, options);

	const progress =
		preview.companyCount > 0
			? Math.round((preview.processedCount / preview.companyCount) * 100)
			: 0;

	return (
		<div className="mt-6 rounded-lg border border-status-warning/30 bg-status-warning/5 p-4">
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<div className="h-2 w-2 rounded-full bg-status-warning animate-pulse" />
					<span className="text-sm font-medium text-text">{ts("inProgress")}</span>
				</div>
				<Button
					color={ButtonColor.DANGER_SECONDARY}
					isLoading={isStopLoading}
					title={ts("stopPreview")}
					onClick={onStop}
				/>
			</div>

			{/* Progress bar */}
			<div className="mb-3">
				<div className="flex items-center justify-between mb-1">
					<span className="text-xs text-text-meta">
						{ts("companiesProcessed", {
							processed: preview.processedCount,
							total: preview.companyCount,
						})}
					</span>
					<span className="text-xs font-medium text-text">{progress}%</span>
				</div>
				<div className="h-2 w-full rounded-full bg-gray-200">
					<div
						className="h-2 rounded-full bg-blue-action transition-all duration-500"
						style={{ width: `${progress}%` }}
					/>
				</div>
			</div>

			{/* Stats counters */}
			<div className="flex items-center gap-6">
				<div className="flex items-center gap-1.5">
					<div className="h-2 w-2 rounded-full bg-status-success" />
					<span className="text-xs text-text-meta">
						{ts("found")}: {preview.foundCount}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="h-2 w-2 rounded-full bg-status-warning" />
					<span className="text-xs text-text-meta">
						{ts("unsure")}: {preview.unsureCount}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<div className="h-2 w-2 rounded-full bg-status-error" />
					<span className="text-xs text-text-meta">
						{ts("notFound")}: {preview.notFoundCount}
					</span>
				</div>
			</div>
		</div>
	);
};
